import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { endpoints } from 'Core/defaultValues';

// Get endpoints from environment
const GRAPHQL_AI_HTTP = endpoints.graphqlAI;
const GRAPHQL_AI_WS = endpoints.graphqlAIWS;

// Validate endpoints are configured
if (!GRAPHQL_AI_HTTP || !GRAPHQL_AI_WS) {
  console.error('❌ AI Chat endpoints not configured. Please check environment variables:', {
    GRAPHQL_AI_HTTP,
    GRAPHQL_AI_WS,
  });
}

// Create AWS AppSync subscription link
// This handles AppSync's custom WebSocket protocol automatically
// Authentication: Token-based only (IdToken from account login)
const appSyncSubscriptionLink = createSubscriptionHandshakeLink(
  {
    url: GRAPHQL_AI_HTTP,
    region: 'us-east-1', // Extract from endpoint
    auth: {
      type: 'OPENID_CONNECT', // JWT token-based authentication
      jwtToken: async () => {
        const token = localStorage.getItem('IdToken');
        if (!token) {
          console.warn('⚠️ No IdToken found in localStorage');
          return '';
        }
        return token;
      },
    },
  },
  new HttpLink({ uri: GRAPHQL_AI_HTTP })
);

console.log('✅ AppSync subscription link created');

// Create HTTP Link for queries and mutations
// Authentication: Token-based only (IdToken from account login)
// Use setContext to get token dynamically on each request
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('IdToken');
  if (!token) {
    console.warn('⚠️ No IdToken found in localStorage for HTTP request');
  }
  return {
    headers: {
      ...headers,
      authorization: token || '', // lowercase 'authorization' to match existing AppSync setup
    }
  };
});

const httpLink = new HttpLink({
  uri: GRAPHQL_AI_HTTP,
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) {
    console.error(`[Network error ${operation.operationName}]: ${networkError.message}`);
  }
});

// Split link: route subscriptions to AppSync subscription link, queries/mutations to HTTP with auth
// The AppSync subscription link handles both HTTP and WebSocket internally
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  appSyncSubscriptionLink, // Use AppSync subscription link for subscriptions
  authLink.concat(httpLink) // Use HTTP with auth for queries/mutations
);

// Create Apollo Client
const appSyncClient = new ApolloClient({
  link: ApolloLink.from([errorLink, splitLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Export cleanup function
// Note: aws-appsync-subscription-link handles connection lifecycle automatically
export const closeWebSocketConnection = () => {
  // The AppSync subscription link manages its own lifecycle
  // Connections are cleaned up when the Apollo Client is stopped
  console.log('✅ AppSync connection cleanup requested');
};

export default appSyncClient;
